/* OutboundSyslogComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const Renderer = require("./OutboundSyslogRenderer");
const _ = require("lodash");

class OutboundSyslogComponent extends EventEmitter {
    constructor(OutboundSyslogEntity, userSession, uiUtils, logger, userFeedback) {
        super();

        this._OutboundSyslogEntity = OutboundSyslogEntity;
        this._userSession = userSession;
        this._logger = logger;
        this._muf = userFeedback;
        this._outboundSyslogEntity = undefined;
        this._uiUtils = uiUtils;

        this._renderer = new Renderer(this, uiUtils);

        // this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _triggerDataUpdated() {
        this.emit("DataUpdated");
    }

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        if (this._outboundSyslogEntity && this._outboundSyslogEntity.shutdown) {
            this._outboundSyslogEntity.shutdown().catch((error) => this._logger.warn({ error }));
        }
        this._muf.hideDisplayed();
        return this._renderer.shutdown().then(
            () => {
                this._OutboundSyslogEntity = null;
                this._userSession = null;
                this._logger = null;
                this._outboundSyslogEntity = null;
                this._data = null;
                this._fieldsKeys = null;
                this._renderer = null;
            },
            (err) => {
                console.log("Error shutting down OutboundSyslogComponent: ", err);
            }
        );
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get OutboundSyslogEntity() {
        return this._OutboundSyslogEntity;
    }

    setOutboundSyslogEntity(newOrgInstance) {
        this._outboundSyslogEntity = newOrgInstance;
        return this._outboundSyslogEntity;
    }

    retrieveOutboundSyslogEntity(tenantId, isAscendCall = true) {
        if (this._outboundSyslogEntity && this._outboundSyslogEntity.shutdown) {
            this._outboundSyslogEntity.shutdown().catch((error) => this._logger.warn({ error }));
        }
        const prom = isAscendCall
            ? this._OutboundSyslogEntity.retrieveAscendSyslogPrefs(tenantId)
            : this._OutboundSyslogEntity.retrieveSyslogPrefs(tenantId);
        return prom.then((prefs) => this.setOutboundSyslogEntity(prefs));
    }

    getOutboundSyslogData(id) {
        if (!this.activeOrgId && !id) {
            return Promise.resolve();
        }
        return this.retrieveOutboundSyslogEntity(id || this.activeOrgId).then((prefsEntity) => {
            // console.log(prefsEntity);
            return prefsEntity;
        });
    }

    isDirty() {
        return this._outboundSyslogEntity.isDirty();
    }

    _setAdvData(data) {
        Object.keys(data).forEach((key) => {
            this._outboundSyslogEntity[key] = data[key];
        });
    }

    enableDisablePrefs(val) {
        let message =
            "This disables logging for this organization. However, if the parent organization has logging enabled, logging for this organization continues.";
        if (!this._getToggleEnable()) {
            // if root org
            message =
                "Disabling logging for this organization also disables logging for any child organization that does not have logging separately enabled.";
        }
        const title = "Disable Logging";
        let veilProm;
        if (!val) {
            veilProm = this._muf.displayConfirmation(message, {
                title,
                confirmDetails: {
                    text: "Confirm",
                    type: "additive"
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text"
                },
                notDismissable: true,
                dontHide: true
            });
        }

        return Promise.all([veilProm])
            .then(() => {
                // return Promise.resolve();
                if (val) {
                    return this.retrieveOutboundSyslogEntity(this.activeOrgId, false).then(() => {
                        this._outboundSyslogEntity.enabled = true;
                        return this._outboundSyslogEntity.update();
                    });
                } else {
                    this._outboundSyslogEntity.enabled = false;
                    return this._outboundSyslogEntity.update().then(() => {
                        return this.retrieveOutboundSyslogEntity(this.activeOrgId, true);
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                return this._muf.hideDisplayed().then(() => {
                    if (err.userRejected) {
                        return Promise.resolve("cancel");
                    }
                    return this._muf.displayError(err).catch(() => Promise.resolve());
                });
            })
            .finally(() => {
                return this._muf.hideDisplayed();
                // return Promise.resolve();
            });
    }

    // testSyslog() {
    //     const prefs = _.cloneDeep(this._outboundSyslogEntity.properties);
    //     let message;
    //     return this._OutboundSyslogEntity.sendTestSyslogMessage(prefs).then((data) => {
    //         console.log(data);
    //         message = "Successfully sent message to host.";
    //     }).catch((error) => {
    //         message = "Failed to send message";
    //         this._logger.warn({error});
    //     }).finally(() => {
    //         this._uiUtils.showMessageDialog({
    //             message: message,
    //             confirmButtonName: "OK"
    //         });
    //     });
    // }

    _validate() {
        let errMessage = "";
        let count = 0;

        if (!this._outboundSyslogEntity.syslogServerAddress) {
            errMessage += "Destination Host is required.\n";
            ++count;
        }

        if (!this._outboundSyslogEntity.syslogServerPort) {
            errMessage += "Destination Port is required.\n";
            ++count;
        }

        if (this._outboundSyslogEntity.syslogServerProtocol !== "udp") {
            if (!this._outboundSyslogEntity.syslogServerTcpTimeout) {
                errMessage += "Timeout is required for TLS and TCP.\n";
                ++count;
            }
        }

        if (
            this._outboundSyslogEntity.syslogServerProtocol === "tls" &&
            !this._outboundSyslogEntity.allowUnauthorizedServerCertificates
        ) {
            const serverCerts = this._outboundSyslogEntity.syslogServerTlsServerCerts;
            if (!serverCerts || !Array.isArray(serverCerts) || serverCerts.length < 1) {
                errMessage += "Server TLS Certificates are required for TLS.\n";
                ++count;
            }
        }

        if (count === 0) {
            return Promise.resolve();
        }

        if (count === 1) {
            errMessage = "Error: " + errMessage;
        } else {
            errMessage = "Errors:\n" + errMessage;
        }
        return Promise.reject(errMessage);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    whenModelIsReady() {
        return this.getOutboundSyslogData().then(() => {
            // if (this._userHasPerms() && !this._getToggleEnable()) {
            //     if (!this._outboundSyslogEntity.enabled) {
            //         this._outboundSyslogEntity.enabled = true;
            //         return this._outboundSyslogEntity.update()
            //             .then(() => {
            //                 this._enabled = this._outboundSyslogEntity.enabled && this._outboundSyslogEntity.tenantId === this.activeOrgId;
            //                 this._registerListeners();
            //                 return Promise.resolve();
            //             });
            //     }
            // }
            this._enabled =
                this._outboundSyslogEntity.enabled &&
                this._outboundSyslogEntity.tenantId === this.activeOrgId;

            this._registerListeners();
            return Promise.resolve();
        });
    }

    organizationChanged({ id }) {
        this._muf.hideDisplayed();
        this.getOutboundSyslogData(id).then(() => this._triggerDataUpdated());
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getToggleEnable() {
        // returns true for non-root orgs
        if (this._userSession && this._userSession._userEntity) {
            return !!_.get(
                this._userSession._userEntity.authorizedTenants,
                `${this.activeOrgId}.providerName`,
                false
            );
        }
    }

    _userHasPerms() {
        if (this._userSession) {
            return this._userSession.authorizedActions.indexOf("outboundSyslogPrefsWrite") >= 0;
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START DATA VALIDATION MEtHODS           */
    /*******************************************/

    /*******************************************/
    /* START DATA VALIDATION MEtHODS           */
    /*******************************************/
}

module.exports = OutboundSyslogComponent;

OutboundSyslogComponent.$groupName = "Organization";

OutboundSyslogComponent.$displayName = "Logging";
OutboundSyslogComponent.$license = ["GLUWARE"];

OutboundSyslogComponent.$inject = [
    "$teve.entities.SyslogPrefs",
    "$teve.userSession",
    "$teve.ui.UiUtils",
    "$teve.logger",
    "$teve.ui.userFeedback"
];
